import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, GraduationCap, UserRound } from 'lucide-react';
import { NAV_LINKS } from '../data/mock';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/UIContext';
import UIControls from './UIControls';
import { preloadRobotModel } from '../lib/preloadModel';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'py-3 bg-background/85 backdrop-blur-xl border-b border-white/5' : 'py-5 bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 group shrink-0" onMouseEnter={preloadRobotModel} onTouchStart={preloadRobotModel}>
          <img src="/dts-brain.png" alt="Digital Tech Solutions" className="w-14 h-14 object-contain transition-transform group-hover:scale-110" />
          <div className="leading-none">
            <span className="font-display font-bold text-[15px] text-white">Digital Tech</span>
            <span className="block text-[10px] tracking-widest text-lime font-mono-t">SOLUTIONS</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              onMouseEnter={l.path === '/' ? preloadRobotModel : undefined}
              className={`px-3 py-2 text-sm rounded-md transition-colors ${location.pathname === l.path ? 'text-lime' : 'text-white/70 hover:text-white'}`}
            >
              {t(l.key, l.label)}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <UIControls />
          <button
            onClick={() => navigate(user ? '/academy' : '/academy/login')}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-lime text-background text-sm font-semibold btn-glow transition-all hover:scale-105"
          >
            <GraduationCap size={16} /> {t('nav.academy', 'Academy')}
          </button>
          <Link to="/clienti" className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-white text-sm hover:border-lime hover:text-lime transition-colors">
            <UserRound size={15} /> {t('nav.clienti', 'Clienti')}
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <UIControls compact />
          <button className="text-white" onClick={() => setOpen(!open)} aria-label="menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mt-3 mx-4 rounded-2xl glass p-4 flex flex-col gap-1">
          {NAV_LINKS.map((l) => (
            <Link key={l.path} to={l.path} className="px-4 py-3 rounded-lg text-white/80 hover:bg-white/5 hover:text-lime transition-colors">{t(l.key, l.label)}</Link>
          ))}
          <button onClick={() => navigate(user ? '/academy' : '/academy/login')} className="mt-2 px-4 py-3 rounded-lg bg-lime text-background font-semibold flex items-center justify-center gap-2">
            <GraduationCap size={16} /> {t('nav.academy', 'Academy')}
          </button>
          <Link to="/clienti" className="px-4 py-3 rounded-lg border border-white/15 text-white font-medium flex items-center justify-center gap-2 hover:border-lime hover:text-lime transition-colors">
            <UserRound size={16} /> {t('nav.clienti', 'Clienti')}
          </Link>
        </div>
      )}
    </header>
  );
}
